import { useMemo, useState, useEffect } from 'react';
import {
  Plus,
  Wallet,
  PiggyBank,
  LogOut,
  Settings,
  Edit,
  Trash2,
  TrendingUp,
  Users,
  ArrowLeft,
  Upload,
} from 'lucide-react';
import type { Income, Expense } from '../types';
import type { ExpenseFormData } from '../components/forms/expense-form';
import type { IncomeFormData } from '../components/forms/income-form';
import { useAuth } from '../hooks/use-auth';
import { useToast } from '../components/ui/toast';
import { useAppData } from '../contexts/app-data-context';
import { useRoute } from '../hooks/use-route';
import type { SectionId } from '../lib/routes';
import { TabsRoot, TabsList, TabsTab, TabsPanel } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { MonthYearSelector } from '../components/common/month-year-selector';
import {
  MenuRoot,
  MenuTrigger,
  MenuPortal,
  MenuPositioner,
  MenuPopup,
  MenuItem,
  MenuSeparator,
} from '../components/ui/menu';
import { PositionsOverview } from '../components/investments/positions-overview';
import { ExpenseList } from '../components/expenses/expense-list';
import {
  Alert,
  BudgetAlertsContainer,
} from '../components/alerts/budget-alert';
import { DashboardCard } from '../components/dashboard/dashboard-card';
import { Footer } from '../components/layout/footer';
import Logo from '../../assets/logo.png';
import { formatCurrency } from '../lib/format';
import { categoryTypes } from '../types';
import { TagsManager } from '../components/admin/tags-manager';
import { CaixinhaSection } from '../components/caixinhas/caixinha-section';
import { ExpenseReports } from '../components/reports/expense-reports';
import { WishlistTab } from '../components/wishlist/wishlist-tab';
import { InvestmentsSection } from '../components/investments/investments-section';
import { GoogleSignInButton } from '../components/common/google-sign-in-button';
import { BudgetDialog } from '../components/dialogs/budget-dialog';
import { IncomeDialog } from '../components/dialogs/income-dialog';
import { ExpenseDialog } from '../components/dialogs/expense-dialog';
import { ImportExpensesDialog } from '../components/dialogs/import-expenses-dialog';
import { getUserProfileByEmail } from '../services/firestore';

const ADMIN_EMAIL = 'gabriellc2307@gmail.com';
const SARAH_EMAIL = 'sarahlourenco.rainhadosanjos@gmail.com';

/**
 * Avança a data em um mês, limitando o dia ao último dia do mês destino
 * (31/01 vira 28/02, e não 03/03 como o rollover padrão do JS).
 */
function addOneMonth(date: Date): Date {
  const year = date.getFullYear();
  const month = date.getMonth();
  const lastDayNextMonth = new Date(year, month + 2, 0).getDate();
  return new Date(
    year,
    month + 1,
    Math.min(date.getDate(), lastDayNextMonth)
  );
}

export function DashboardPage() {
  const {
    user,
    loading: authLoading,
    signInWithGoogle,
    signOut,
    error: authError,
    clearError,
  } = useAuth();
  const { showToast } = useToast();
  const {
    section,
    investmentsTab,
    unknown: unknownRoute,
    goToSection,
    replaceWith,
  } = useRoute();

  useEffect(() => {
    if (authError === 'unauthorized') {
      showToast('Acesso não autorizado.', 'error');
      clearError();
    }
  }, [authError, showToast, clearError]);

  // Limpa a barra de endereço sem sujar o histórico. Numa URL meio certa
  // ("/investimentos/zzz") vale mais cair na seção pedida do que no dashboard.
  const isAdmin = user?.email === ADMIN_EMAIL;
  useEffect(() => {
    if (section === 'admin' && user && !isAdmin) {
      replaceWith('dashboard');
    } else if (unknownRoute) {
      replaceWith(section, investmentsTab);
    }
  }, [unknownRoute, section, investmentsTab, user, isAdmin, replaceWith]);

  const {
    budget,
    originalBudget,
    expenses,
    allExpenses,
    incomes,
    totalIncome,
    positions,
    portfolioSummary,
    netWorth,
    monthEarnings,
    totalExpenses,
    balance,
    addExpense,
    addIncome,
    updateIncome,
    updateExpense,
    deleteExpense,
    deleteIncome,
    tags,
    addTag,
    updateTag,
    deleteTag,
    bitcoinPrice,
    loadingBitcoinPrice,
    viewingAs,
    setViewingAs,
  } = useAppData();

  const handleSwitchAccount = async () => {
    if (viewingAs) {
      setViewingAs(null);
    } else {
      const profile = await getUserProfileByEmail(SARAH_EMAIL);
      if (profile) setViewingAs(profile);
    }
  };

  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [incomeDialogOpen, setIncomeDialogOpen] = useState(false);
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [editingIncome, setEditingIncome] = useState<Income | null>(null);
  const [incomeToDelete, setIncomeToDelete] = useState<Income | null>(null);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [expenseToDelete, setExpenseToDelete] = useState<Expense | null>(null);


  // Mock alerts
  const alerts = useMemo((): Alert[] => {
    const all = [];
    if (incomes.length === 0) {
      all.push({
        id: 'income-message-1',
        severity: 'income' as const,
        title: 'Antes de gastar…',
        message: 'Bora cadastrar a primeira renda do mês',
        onClick: () => setIncomeDialogOpen(true),
      });
    }
    if (originalBudget === null) {
      all.push({
        id: 'budget-message-1',
        severity: 'budget' as const,
        title: 'Só pra ter controle...',
        message: 'Bora definir o orçamento do mês',
        onClick: () => setBudgetDialogOpen(true),
      });
    }
    return all;
  }, [incomes, originalBudget]);

  const handleExpenseSubmit = async (data: ExpenseFormData) => {
    try {
      if (editingExpense) {
        await updateExpense(editingExpense.id, data);
      } else {
        await addExpense(data);
      }
      setExpenseDialogOpen(false);
      setEditingExpense(null);
    } catch (error) {
      console.error('Erro ao salvar gasto:', error);
    }
  };

  const handleIncomeSubmit = async (data: IncomeFormData) => {
    try {
      if (editingIncome) {
        await updateIncome(editingIncome.id, data);
      } else {
        await addIncome(data);
      }
      setIncomeDialogOpen(false);
      setEditingIncome(null);
    } catch (error) {
      console.error('Erro ao salvar renda:', error);
    }
  };

  const handleEditIncome = (income: Income) => {
    setEditingIncome(income);
    setIncomeDialogOpen(true);
  };

  const handleDeleteIncomeConfirm = async () => {
    if (incomeToDelete) {
      try {
        await deleteIncome(incomeToDelete.id);
        setIncomeToDelete(null);
      } catch (error) {
        console.error('Erro ao deletar renda:', error);
      }
    }
  };

  const handleIncomeDialogClose = () => {
    setIncomeDialogOpen(false);
    setEditingIncome(null);
  };

  const handleEditExpense = (expense: Expense) => {
    setEditingExpense(expense);
    setExpenseDialogOpen(true);
  };

  const handleExpenseDialogClose = () => {
    setExpenseDialogOpen(false);
    setEditingExpense(null);
  };

  const handleDeleteExpenseConfirm = async () => {
    if (expenseToDelete) {
      try {
        await deleteExpense(expenseToDelete.id);
        setExpenseToDelete(null);
      } catch (error) {
        console.error('Erro ao deletar gasto:', error);
      }
    }
  };

  const handleRepeatExpense = async (expense: Expense) => {
    const nextDate = addOneMonth(new Date(expense.date));
    try {
      await addExpense({
        title: expense.title,
        description: expense.description,
        value: expense.value,
        date: nextDate,
        categoryType: expense.categoryType,
        tags: expense.tags ?? [],
      });
      showToast(
        `"${expense.title}" repetido para ${nextDate.toLocaleDateString('pt-BR', {
          month: 'long',
          year: 'numeric',
        })}`,
        'success'
      );
    } catch (error) {
      console.error('Erro ao repetir gasto:', error);
      showToast('Não foi possível repetir o gasto.', 'error');
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <main className="flex-1 p-6">
        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="flex items-center">
                <img
                  src={Logo}
                  alt="icon"
                  className="w-[50] mr-1"
                  style={{
                    width: 180,
                  }}
                />
              </div>
              <h1 className="text-sm text-foreground-subtle">
                Tenha controle dos seus gastos mensais
              </h1>
            </div>

            <div className="flex items-center gap-4">
              {user && (
                <MenuRoot>
                  <MenuTrigger
                    render={
                      <button
                        type="button"
                        className="flex size-9 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-primary text-sm font-medium text-primary-foreground transition-opacity hover:opacity-80"
                      >
                        {user.photoURL ? (
                          <img
                            src={user.photoURL}
                            alt={user.displayName || 'Avatar'}
                            className="size-full object-cover"
                          />
                        ) : (
                          <span>
                            {(
                              user.displayName?.[0] ||
                              user.email?.[0] ||
                              'U'
                            ).toUpperCase()}
                          </span>
                        )}
                      </button>
                    }
                  />
                  <MenuPortal>
                    <MenuPositioner sideOffset={8}>
                      <MenuPopup>
                        {/* User info */}
                        <div className="px-3 py-2">
                          {user.displayName && (
                            <p className="text-sm font-medium text-foreground">
                              {user.displayName}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {user.email}
                          </p>
                        </div>
                        <MenuSeparator />
                        <MenuItem onClick={() => setBudgetDialogOpen(true)}>
                          <Settings className="size-4" />
                          Configurar orçamento
                        </MenuItem>
                        {user.email === ADMIN_EMAIL && (
                          <>
                            <MenuSeparator />
                            <MenuItem onClick={handleSwitchAccount}>
                              <Users className="size-4" />
                              {viewingAs ? 'Voltar para minha conta' : 'Ver como Sarah'}
                            </MenuItem>
                          </>
                        )}
                        <MenuSeparator />
                        <MenuItem onClick={signOut}>
                          <LogOut className="size-4" />
                          Sair
                        </MenuItem>
                      </MenuPopup>
                    </MenuPositioner>
                  </MenuPortal>
                </MenuRoot>
              )}
            </div>
          </div>

          {/* Sticky action bar */}
          <TabsRoot
            value={section}
            onValueChange={(value) => goToSection(value as SectionId)}
          >
          <div className="sticky top-0 z-20 -mx-6 flex flex-col gap-2 border-b border-border bg-surface/95 px-6 py-3 mb-6 backdrop-blur-sm">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <MonthYearSelector />
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
              <div className="flex items-center gap-2 self-start rounded-md border border-border bg-white px-2.5 py-1.5 sm:self-auto">
                <span className="text-xs font-bold text-orange-500">₿</span>
                {loadingBitcoinPrice ? (
                  <span className="text-xs text-muted-foreground">...</span>
                ) : bitcoinPrice ? (
                  <>
                    <span className="text-xs font-medium text-foreground">
                      R$ {bitcoinPrice.brl.toLocaleString('pt-BR')}
                    </span>
                    <span className="text-xs text-muted-foreground">·</span>
                    <span className="text-xs text-muted-foreground">
                      $ {bitcoinPrice.usd.toLocaleString('en-US')}
                    </span>
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <Button
                  variant="outline"
                  className="flex-1 sm:flex-none"
                  onClick={() => setImportDialogOpen(true)}
                >
                  <Upload className="size-4" />
                  Importar
                </Button>
                <Button
                  className="flex-1 sm:flex-none"
                  onClick={() => setExpenseDialogOpen(true)}
                >
                  <Plus className="size-4" />
                  Novo gasto
                </Button>
              </div>
            </div>
            </div>
            <TabsList>
              <TabsTab value="dashboard">Dashboard</TabsTab>
              <TabsTab value="expenses">Gastos</TabsTab>
              <TabsTab value="reports">Relatórios</TabsTab>
              <TabsTab value="investments">Investimentos</TabsTab>
              <TabsTab value="wishlist">Desejos</TabsTab>
              {user?.email === ADMIN_EMAIL && (
                <TabsTab value="admin">Admin</TabsTab>
              )}
            </TabsList>
          </div>

          {/* Impersonation banner */}
          {viewingAs && (
            <div className="mb-6 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5">
              <div className="flex items-center gap-2">
                <Users className="size-4 text-amber-600 shrink-0" />
                <p className="text-sm text-amber-800">
                  Visualizando como <strong>{viewingAs.displayName}</strong>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setViewingAs(null)}
                className="flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:text-amber-900"
              >
                <ArrowLeft className="size-3.5" />
                Voltar para minha conta
              </button>
            </div>
          )}

          {/* Login message */}
          {!user && !authLoading && (
            <div className="mb-6 flex items-center justify-between rounded-lg border border-primary/20 bg-primary/5 p-4">
              <p className="text-sm text-foreground">
                Faça o login para salvar seus dados e acessar de qualquer
                dispositivo.
              </p>
              <GoogleSignInButton onClick={signInWithGoogle} />
            </div>
          )}

          {/* Alerts */}
          <BudgetAlertsContainer alerts={alerts} className="mb-6" />

            {/* Dashboard Tab */}
            <TabsPanel value="dashboard">
              <div className="grid gap-6">
                {/* Resumo do mês - linha simples */}
                <div className="flex flex-wrap items-center gap-6 rounded-lg border border-border bg-white p-4">
                  <div className="flex items-center gap-2">
                    <Wallet className="size-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Saldo do mês
                    </span>
                    <span
                      className={`text-xl font-bold ${balance >= 0 ? 'text-success' : 'text-destructive'}`}
                    >
                      R$ {formatCurrency(balance)}
                    </span>
                  </div>
                  <div className="h-6 w-px bg-border" />
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      Total gasto
                    </span>
                    <span className="text-xl font-bold text-foreground">
                      R$ {formatCurrency(totalExpenses)}
                    </span>
                  </div>
                  <div className="h-6 w-px bg-border" />
                  <div className="flex items-center gap-2">
                    <TrendingUp className="size-5 text-muted-foreground" />
                    <span
                      className="text-sm text-muted-foreground"
                      title="Caixinhas + bitcoin + carteira de investimentos"
                    >
                      Patrimônio
                    </span>
                    <span className="text-xl font-bold text-foreground">
                      R$ {formatCurrency(netWorth)}
                    </span>
                  </div>
                </div>

                {/* Investimentos - posição atual */}
                <DashboardCard
                  title="Investimentos"
                  count={positions.length}
                  icon={<TrendingUp />}
                  iconLabel="Ver carteira"
                  onIconClick={() => goToSection('investments')}
                  description={
                    positions.length === 0
                      ? undefined
                      : `Valor atual: R$ ${formatCurrency(portfolioSummary.marketValue)} · ${
                          portfolioSummary.unrealizedGain >= 0 ? '+' : '-'
                        }R$ ${formatCurrency(Math.abs(portfolioSummary.unrealizedGain))} (${
                          portfolioSummary.unrealizedGainPct >= 0 ? '+' : ''
                        }${portfolioSummary.unrealizedGainPct.toFixed(1)}%)${
                          monthEarnings > 0
                            ? ` · Proventos no mês: R$ ${formatCurrency(monthEarnings)}`
                            : ''
                        }`
                  }
                  className="overflow-hidden"
                  collapsible
                >
                  <PositionsOverview
                    positions={positions}
                    partialPricing={portfolioSummary.partialPricing}
                  />
                </DashboardCard>

                {/* Rendas do mês */}
                <DashboardCard
                  title="Rendas do mês"
                  count={incomes.length}
                  icon={<PiggyBank />}
                  iconLabel="Add renda"
                  onIconClick={() => setIncomeDialogOpen(true)}
                  collapsible
                >
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        Total de Rendas
                      </span>
                      <span className="text-xl font-bold text-success">
                        R$ {formatCurrency(totalIncome)}
                      </span>
                    </div>

                    {incomes.length > 0 && (
                      <div className="flex flex-col gap-2 border-t border-border pt-3">
                        {incomes.map((income) => (
                          <div
                            key={income.id}
                            className="flex flex-col items-center justify-between rounded-md border border-border p-2"
                          >
                            <span className="text-sm text-foreground w-full line-clamp-2">
                              {income.title}
                            </span>
                            <div className="flex items-center gap-2 justify-between w-full">
                              <span className="text-sm font-medium text-success">
                                R$ {formatCurrency(income.value)}
                              </span>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditIncome(income)}
                                  className="text-muted-foreground hover:text-primary p-1"
                                  title="Editar"
                                >
                                  <Edit className="size-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setIncomeToDelete(income)}
                                  className="text-muted-foreground hover:text-destructive p-0"
                                  title="Excluir"
                                >
                                  <Trash2 className="size-3.5" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {incomes.length === 0 && (
                      <p className="text-sm text-muted-foreground">
                        Nenhuma renda cadastrada
                      </p>
                    )}
                  </div>
                </DashboardCard>

                {/* Gastos por categoria */}
                <DashboardCard
                  title="Gastos por categoria"
                  icon={<Settings />}
                  iconLabel="Configurar orçamento"
                  onIconClick={() => setBudgetDialogOpen(true)}
                  collapsible
                >
                  <div className="flex flex-col gap-4">
                    {(['needs', 'comfort'] as const).map((key) => {
                      const info = categoryTypes[key];
                      const spent = expenses
                        .filter((e) => e.categoryType === key)
                        .reduce((s, e) => s + e.value, 0);
                      const goal = budget[key];
                      const pct =
                        goal > 0 ? Math.min((spent / goal) * 100, 100) : 0;
                      const remaining = goal - spent;
                      const over = remaining < 0;
                      return (
                        <div key={key} className="flex flex-col gap-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span
                                className="size-2.5 shrink-0 rounded-full"
                                style={{ backgroundColor: info.color }}
                              />
                              <span className="text-sm font-medium text-foreground">
                                {info.label}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-foreground">
                                R$ {formatCurrency(spent)}
                              </span>
                              {goal > 0 && (
                                <span className="text-xs text-foreground-subtle">
                                  / R$ {formatCurrency(goal)}
                                </span>
                              )}
                            </div>
                          </div>
                          {goal > 0 && (
                            <>
                              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{
                                    width: `${pct}%`,
                                    backgroundColor: info.color,
                                  }}
                                />
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">
                                  {pct.toFixed(0)}% usado
                                </span>
                                <span
                                  className={
                                    over ? 'text-destructive' : 'text-success'
                                  }
                                >
                                  R$ {formatCurrency(Math.abs(remaining))}{' '}
                                  {over ? 'excedido' : 'restante'}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </DashboardCard>

                {/* Caixinhas */}
                <CaixinhaSection />
              </div>
            </TabsPanel>

            {/* Expenses Tab */}
            <TabsPanel value="expenses">
              <ExpenseList
                expenses={expenses}
                onEdit={handleEditExpense}
                onDelete={(expense) => setExpenseToDelete(expense)}
                onRepeat={handleRepeatExpense}
                availableTags={tags}
              />
            </TabsPanel>

            {/* Reports Tab */}
            <TabsPanel value="reports">
              <ExpenseReports expenses={allExpenses} availableTags={tags} />
            </TabsPanel>

            {/* Investments Tab */}
            <TabsPanel value="investments">
              <InvestmentsSection />
            </TabsPanel>

            {/* Wishlist Tab */}
            <TabsPanel value="wishlist">
              <WishlistTab />
            </TabsPanel>

            {user?.email === ADMIN_EMAIL && (
              <TabsPanel value="admin">
                <div className="flex flex-col gap-6 max-w-lg">
                  <h2 className="text-lg font-semibold text-foreground">
                    Tags
                  </h2>
                  <TagsManager
                    tags={tags}
                    onAdd={addTag}
                    onUpdate={updateTag}
                    onDelete={deleteTag}
                  />
                </div>
              </TabsPanel>
            )}
          </TabsRoot>
        </div>

        {/* <div className="border-t border-border px-6 mt-6">
          <div className="mx-auto max-w-4xl">
            <AboutArticle />
          </div>
        </div> */}
      </main>
      <Footer />

      {/* Dialogs */}
      <BudgetDialog
        open={budgetDialogOpen}
        onOpenChange={setBudgetDialogOpen}
      />
      <IncomeDialog
        open={incomeDialogOpen}
        editingIncome={editingIncome}
        onSubmit={handleIncomeSubmit}
        onClose={handleIncomeDialogClose}
        incomeToDelete={incomeToDelete}
        onDeleteConfirm={handleDeleteIncomeConfirm}
        onDeleteCancel={() => setIncomeToDelete(null)}
      />
      <ExpenseDialog
        open={expenseDialogOpen}
        editingExpense={editingExpense}
        onSubmit={handleExpenseSubmit}
        onClose={handleExpenseDialogClose}
        tags={tags}
        expenseToDelete={expenseToDelete}
        onDeleteConfirm={handleDeleteExpenseConfirm}
        onDeleteCancel={() => setExpenseToDelete(null)}
      />
      <ImportExpensesDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        addExpense={addExpense}
      />
    </div>
  );
}
